import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDateTime, formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import type { ReportDocument, ReportDocumentCell, ReportDocumentTable } from './report-document.js';

/**
 * NR496 raporunun PDF'i (plan M7 Task 29; M10f kalıbı, pdfmake, internetsiz): A4 yatay.
 * - Başlık: şirket markası, rapor adı, gemi, dönem, süzgeç, raporun kaynağı ve dayanağı.
 * - Altlık: "Felixmate" ve sürüm, basım zamanı (UTC ve gemi saati), basan kişinin adı ve
 *   rolü, "Page X of Y" ve rapor verisinin hash'i [NR496 4.1.2].
 * - İptal edilen kayıt "VOID" işaretiyle ve gerekçesiyle basılır [NR496 4.5.3f].
 * Saf fonksiyon; baytları `renderPdf` üretir.
 */

const FONT = 'Roboto';

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 2,
  paddingRight: () => 2,
  paddingTop: () => 2,
  paddingBottom: () => 2,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

const numeric = (kind: string) => kind === 'number' || kind === 'hours' || kind === 'percent';

function cellText(kind: string, value: ReportDocumentCell, locale: string): string {
  if (value === null || value === '') return '—';
  if (kind === 'date' && typeof value === 'string') return formatDay(value);
  if (kind === 'datetime' && typeof value === 'string') return formatDateTime(value, 0);
  if (typeof value === 'number') {
    return value.toLocaleString(locale, { maximumFractionDigits: kind === 'hours' ? 1 : 3 });
  }
  return String(value);
}

function tableContent(doc: ReportDocument, table: ReportDocumentTable): Content[] {
  const head: Content[] = [{ text: table.title, style: 'section', margin: [0, 10, 0, 4] }];
  if (table.rows.length === 0) {
    return [...head, { text: doc.empty, style: 'muted' }];
  }
  const withMark = table.markLabels.some((m) => m !== null);
  const widths = [
    ...(withMark ? ['auto'] : []),
    ...table.columns.map((column) =>
      column.kind === 'text' && table.columns.length < 12 ? '*' : 'auto',
    ),
  ];
  const body: TableCell[][] = [
    [
      ...(withMark ? [{ text: '', style: 'th' } as TableCell] : []),
      ...table.columns.map(
        (column): TableCell => ({
          text: column.label,
          style: 'th',
          alignment: numeric(column.kind) ? 'right' : 'left',
        }),
      ),
    ],
    ...table.rows.map((row, index) => {
      const mark = table.marks[index] ?? null;
      const voided = mark === 'void';
      return [
        ...(withMark
          ? [
              {
                text: table.markLabels[index] ?? '',
                bold: true,
                color: voided ? TOKENS['danger-fg'] : TOKENS['text-secondary'],
              } as TableCell,
            ]
          : []),
        ...table.columns.map(
          (column, i): TableCell => ({
            text: cellText(column.kind, row[i] ?? null, doc.locale),
            alignment: numeric(column.kind) ? 'right' : 'left',
            ...(voided ? { color: TOKENS['text-secondary'] } : {}),
          }),
        ),
      ];
    }),
  ];
  return [
    ...head,
    {
      table: { headerRows: 1, widths, body, dontBreakRows: true },
      layout: LINES,
      // Geniş tablo (History raporu 4.6.2a sütunları) A4 yatayda sığsın diye küçük yazı
      fontSize: table.columns.length > 16 ? 5 : 7,
    },
  ];
}

export function pmsReportPdf(doc: ReportDocument): TDocumentDefinitions {
  const period =
    doc.from && doc.to
      ? doc.from === doc.to
        ? formatDay(doc.from)
        : `${formatDay(doc.from)} – ${formatDay(doc.to)}`
      : formatDay(doc.asOf);
  const content: Content[] = [
    {
      columns: [
        {
          width: '*',
          stack: [
            ...(doc.brand ? [{ text: doc.brand, style: 'company' }] : []),
            { text: doc.title, style: 'title' },
            { text: doc.vessel, style: 'vessel' },
          ],
        },
        {
          width: 'auto',
          stack: [
            { text: `${doc.labels.period}: ${period}`, alignment: 'right' },
            ...(doc.equipment ? [{ text: doc.equipment, alignment: 'right' as const }] : []),
            ...(doc.filters
              ? [{ text: `${doc.labels.filters}: ${doc.filters}`, alignment: 'right' as const }]
              : []),
          ],
          style: 'muted',
        },
      ],
    },
    {
      margin: [0, 6, 0, 0],
      stack: [
        { text: `${doc.labels.source}: ${doc.basis} (${doc.sources})`, style: 'muted' },
        { text: `${doc.labels.rule}: ${doc.rule}`, style: 'muted' },
        ...doc.notes.map((note) => ({ text: note, style: 'note' })),
      ],
    },
    ...doc.tables.flatMap((table) => tableContent(doc, table)),
  ];
  if (doc.summary.length > 0) {
    content.push({ text: doc.labels.summary, style: 'section', margin: [0, 10, 0, 4] });
    content.push({
      table: {
        widths: ['auto', 'auto'],
        body: doc.summary.map((item): TableCell[] => [
          { text: item.label, style: 'muted' },
          { text: item.value, bold: true, alignment: 'right' },
        ]),
      },
      layout: 'noBorders',
    });
  }
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 28, 28, 44],
    info: {
      title: doc.title,
      author: doc.product,
      creator: `${doc.product} ${doc.version}`,
      producer: `${doc.product} ${doc.version}`,
    },
    defaultStyle: { font: FONT, fontSize: 8, color: TOKENS['navy-900'] },
    styles: {
      company: { fontSize: 10, bold: true },
      title: { fontSize: 14, bold: true, color: TOKENS['primary-600'] },
      vessel: { fontSize: 9, bold: true },
      section: { fontSize: 9, bold: true },
      muted: { color: TOKENS['text-secondary'] },
      note: { color: TOKENS['warning-fg'], italics: true },
      th: { bold: true, color: TOKENS['text-secondary'] },
    },
    content,
    footer: (page, pages) => ({
      margin: [28, 8, 28, 0],
      stack: [
        {
          columns: [
            { text: doc.footer, fontSize: 6.5, style: 'muted', width: '*' },
            {
              text: doc.pageTemplate
                .replace('%PAGE%', String(page))
                .replace('%PAGES%', String(pages)),
              alignment: 'right',
              fontSize: 6.5,
              style: 'muted',
              width: 'auto',
            },
          ],
        },
        { text: doc.hash, fontSize: 5.5, style: 'muted' },
      ],
    }),
  };
}
